// src/index.tsx
import { applyPalette, GIFEncoder, quantize } from "gifenc";
import {
  CELL_HEIGHT,
  CELL_WIDTH,
  getDirectionFromVector,
  getDirectionLocation,
  getFrameAtElapsed,
  getFrameLocation,
  getStateSpec
} from "codexpetlab-core";
import { useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load spritesheet: ${src}`));
    image.src = src;
  });
}
function drawFrame(canvas, image, state, elapsed, directionDegrees) {
  const ratio = Math.max(1, window.devicePixelRatio || 1);
  if (canvas.width !== CELL_WIDTH * ratio || canvas.height !== CELL_HEIGHT * ratio) {
    canvas.width = CELL_WIDTH * ratio;
    canvas.height = CELL_HEIGHT * ratio;
  }
  const context = canvas.getContext("2d");
  if (!context) return 0;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, CELL_WIDTH, CELL_HEIGHT);
  context.imageSmoothingEnabled = false;
  const location = directionDegrees == null ? getFrameLocation(state, getFrameAtElapsed(state, elapsed)) : getDirectionLocation(directionDegrees);
  context.drawImage(image, location.x, location.y, location.width, location.height, 0, 0, CELL_WIDTH, CELL_HEIGHT);
  return location.column;
}
function SpritePlayer({
  src,
  state = "idle",
  directionDegrees = null,
  playing = true,
  width = CELL_WIDTH,
  className,
  style,
  onFrame,
  onLoad,
  "aria-label": ariaLabel
}) {
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  useEffect(() => {
    let active = true;
    loadImage(src).then((loaded) => {
      if (!active) return;
      setImage(loaded);
      onLoad?.(loaded);
    });
    return () => {
      active = false;
    };
  }, [src, onLoad]);
  useEffect(() => {
    if (!image || !canvasRef.current) return;
    let animationFrame = 0;
    const startedAt = performance.now();
    const render = (now) => {
      const frame = drawFrame(canvasRef.current, image, state, playing ? now - startedAt : 0, directionDegrees);
      onFrame?.(frame);
      if (playing && directionDegrees == null) animationFrame = requestAnimationFrame(render);
    };
    render(startedAt);
    return () => cancelAnimationFrame(animationFrame);
  }, [directionDegrees, image, onFrame, playing, state]);
  return /* @__PURE__ */ jsx(
    "canvas",
    {
      ref: canvasRef,
      className,
      style: { width, height: width * CELL_HEIGHT / CELL_WIDTH, imageRendering: "pixelated", ...style },
      "aria-label": ariaLabel ?? `${state} pet animation`,
      role: "img"
    }
  );
}
function DirectionPlayground({ deadzone = 20, onDirectionChange, ...playerProps }) {
  const [degrees, setDegrees] = useState(null);
  const updateDirection = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const direction = getDirectionFromVector(event.clientX - (rect.left + rect.width / 2), event.clientY - (rect.top + rect.height / 2), deadzone);
    const next = direction?.degrees ?? null;
    setDegrees(next);
    onDirectionChange?.(next);
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "codexpetlab-direction-playground",
      onPointerMove: updateDirection,
      onPointerLeave: () => {
        setDegrees(null);
        onDirectionChange?.(null);
      },
      style: { position: "relative", touchAction: "none" },
      children: [
        /* @__PURE__ */ jsx(SpritePlayer, { ...playerProps, state: "idle", directionDegrees: degrees }),
        /* @__PURE__ */ jsx("output", { style: { position: "absolute", right: 8, bottom: 8, font: "11px ui-monospace, monospace" }, children: degrees == null ? "neutral" : `${degrees}\xB0` })
      ]
    }
  );
}
async function exportStateGif({ src, state, scale = 1, repeat = 0 }) {
  const image = await loadImage(src);
  const spec = getStateSpec(state);
  const width = Math.round(CELL_WIDTH * scale);
  const height = Math.round(CELL_HEIGHT * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Canvas 2D is unavailable");
  context.imageSmoothingEnabled = false;
  const encoder = GIFEncoder();
  for (let frame = 0; frame < spec.frameCount; frame += 1) {
    const location = getFrameLocation(state, frame);
    context.clearRect(0, 0, width, height);
    context.drawImage(image, location.x, location.y, location.width, location.height, 0, 0, width, height);
    const rgba = context.getImageData(0, 0, width, height).data;
    const palette = quantize(rgba, 256, { format: "rgba4444", oneBitAlpha: true });
    const indexed = applyPalette(rgba, palette, "rgba4444");
    encoder.writeFrame(indexed, width, height, {
      palette,
      delay: spec.durations[frame],
      repeat,
      transparent: true,
      dispose: 2
    });
  }
  encoder.finish();
  const encoded = encoder.bytes();
  const output = new Uint8Array(encoded.byteLength);
  output.set(encoded);
  return new Blob([output.buffer], { type: "image/gif" });
}
function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1e3);
}
export {
  DirectionPlayground,
  SpritePlayer,
  downloadBlob,
  exportStateGif
};
